import { useState, useCallback, useContext } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getCurrentEmployee } from '../service/CorporateWellDhanService';
import { modifyUserProfile } from '../service/AuthWellDhanService';
import { useUtils } from '../context/UtilsContext';
import { ApiContext } from '../context/MyContext';

/**
 * Custom hook to verify employee status and update TOS if needed
 * 
 * Flow:
 * 1. Extract user_id from JWT token
 * 2. Get event_type_id and tos_id from sessionUtils
 * 3. Call getCurrentEmployee()
 * 4. If user not found, update TOS via modifyUserProfile
 * 5. Re-fetch employee data after TOS update
 * 
 * @returns {Object} { employeeData, loading, error, verifyEmployee }
 */
export const useEmployeeVerification = () => {
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { sessionUtils } = useUtils();
  const context = useContext(ApiContext);
  const setCurrentEmployeeData = context?.setCurrentEmployeeData;

  /**
   * Extract user_id from JWT token
   */
  const getUserIdFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return null;
      }
      const decoded = jwtDecode(token);
      // Try common JWT fields for user_id
      return decoded.user_id || decoded.sub || decoded.id || decoded.userId || null;
    } catch (err) {
      console.error('Error decoding JWT token:', err);
      return null;
    }
  }, []);

  /**
   * Get event_type_id for modify_user_profile from sessionUtils
   */
  const getModifyUserProfileEventTypeId = useCallback(() => {
    if (!sessionUtils?.event_types?.[0]?.event_types) {
      return null;
    }
    const eventType = sessionUtils.event_types[0].event_types.find(
      (x) => x.description === 'modify_user_profile'
    );
    return eventType?.id || null;
  }, [sessionUtils]);

  /**
   * Get TOS ID from sessionUtils
   */
  const getTosId = useCallback(() => {
    return sessionUtils?.terms_of_service?.[0]?.id || null;
  }, [sessionUtils]);

  /**
   * Update TOS for the user
   */
  const updateTos = useCallback(async (userId, eventTypeId, tosId) => {
    if (!userId || !eventTypeId || !tosId) {
      throw new Error('Missing required parameters for TOS update');
    }

    const payload = {
      event_type_id: eventTypeId,
      user_id: userId,
      tos: [
        {
          operation: 'ADD',
          tos_id: tosId,
        },
      ],
    };

    const result = await modifyUserProfile(payload);
    if (!result.success) {
      throw new Error(result.message || 'Failed to update TOS');
    }
    return result;
  }, []);

  /**
   * Main verification function
   */
  const verifyEmployee = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Step 1: Get user_id from JWT token
      const userId = getUserIdFromToken();
      if (!userId) {
        throw new Error('Unable to extract user_id from token');
      }

      // Step 2: Get event_type_id and tos_id from sessionUtils
      const eventTypeId = getModifyUserProfileEventTypeId();
      const tosId = getTosId();

      if (!eventTypeId) {
        console.warn('modify_user_profile event_type_id not found in sessionUtils');
      }
      if (!tosId) {
        console.warn('TOS ID not found in sessionUtils');
      }

      // Step 3: Call getCurrentEmployee
      const employeeResult = await getCurrentEmployee();

      if (employeeResult.success) {
        // User found, set employee data
        setEmployeeData(employeeResult.data);
        // Store in context for global access
        if (setCurrentEmployeeData) {
          setCurrentEmployeeData(employeeResult.data);
        }
        setLoading(false);
        return { success: true, data: employeeResult.data };
      }
    
      // Step 4: If user not found, update TOS
      if (employeeResult.userNotFound && eventTypeId && tosId) {
        try {
          await updateTos(userId, eventTypeId, tosId);
          
          // Step 5: Re-fetch employee data after TOS update
          const retryResult = await getCurrentEmployee();
          if (retryResult.success) {
            setEmployeeData(retryResult.data);
            // Store in context for global access
            if (setCurrentEmployeeData) {
              setCurrentEmployeeData(retryResult.data);
            }
            setLoading(false);
            return { success: true, data: retryResult.data };
          } else {
            throw new Error(retryResult.message || 'Failed to fetch employee after TOS update');
          }
        } catch (tosError) {
          console.error('TOS update failed:', tosError);
          setError(tosError.message || 'Failed to update TOS');
          setLoading(false);
          return { success: false, error: tosError.message };
        }
      } else {
        // User not found but missing required data for TOS update
        const errorMsg = employeeResult.message || 'Employee not found and unable to update TOS';
        setError(errorMsg);
        setLoading(false);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      console.error('Employee verification failed:', err);
      const errorMessage = err.message || 'Failed to verify employee';
      setError(errorMessage);
      setLoading(false);
      return { success: false, error: errorMessage };
    }
  }, [getUserIdFromToken, getModifyUserProfileEventTypeId, getTosId, updateTos, setCurrentEmployeeData]);

  return {
    employeeData,
    loading,
    error,
    verifyEmployee,
  };
};
